import type { BomItem, EquipmentCatalogEntry } from '../types/supplyChain';
import { oemBasisFromEquipmentCatalogEntry } from './supplyChain/oemGenerationBomAdapter';

function tokenize(s: string): string[] {
  return s.toLowerCase().split(/[^a-z0-9]+/).filter(t => t.length > 2);
}

function overlapScore(a: string[], b: string[]): number {
  if (a.length === 0 || b.length === 0) return 0;
  const setB = new Set(b);
  let hit = 0;
  for (const t of a) if (setB.has(t)) hit += 1;
  return hit / Math.max(a.length, b.length);
}

export function matchCatalogToBomItem(
  item: BomItem,
  catalog: readonly EquipmentCatalogEntry[],
  limit = 5,
  reactorVendorId?: string,
): EquipmentCatalogEntry[] {
  const itemTokens = tokenize(`${item.name} ${item.spec ?? ''} ${item.category}`);
  const scored = catalog
    .filter(e => e.scmCategory === item.category || e.tags.some(t => itemTokens.includes(t.replace(/-/g, ''))))
    .map((entry) => {
      const entryTokens = tokenize(`${entry.name} ${entry.spec}`);
      let score = overlapScore(itemTokens, entryTokens);
      if (
        reactorVendorId
        && entry.reactorVendorIds?.includes(reactorVendorId)
      ) {
        score += 0.35;
      }
      return { entry, score };
    })
    .filter(x => x.score > 0.15)
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map(x => x.entry);
}

export function catalogEntryToBomDraft(
  entry: EquipmentCatalogEntry,
): Omit<BomItem, 'id' | 'derivedFrom'> {
  const oemBasis = oemBasisFromEquipmentCatalogEntry(entry);
  const hasLeadTime = entry.leadTimeMonths != null;
  const isOemSource = entry.source === 'oem-source-register';
  // B2 — provenance fix for the dead ternary that returned 'industry-baseline'
  // on BOTH branches. The bug: a price-less catalog row was stamped
  // 'industry-baseline', which made #28's CAPEX rollup + provenance surfaces
  // treat an UNPRICED line as a priced industry baseline.
  //
  // Correct provenance: every line built here came FROM the equipment catalog,
  // so the source is 'catalog' (#30, now in the PriceSource union) — never the
  // fake 'industry-baseline'. Whether the catalog row carried a price is
  // captured by `unitCostUsd`/`totalCostUsd` (null when price-less), which is
  // what the cost rollup keys off — so a price-less catalog line is excluded
  // from priced-CAPEX even though its source is 'catalog'.
  //
  // NOTE: the literal intent ("price-less ⇒ priceSource undefined") needs
  // `BomItem.priceSource` to be OPTIONAL. It is required today (yet already
  // nullable in practice — `supplyChainPlanReady` reads `priceSource == null`).
  // `src/types/supplyChain.ts` and the `onCreateItem` caller in
  // `SupplyChainBomPanel.tsx` are outside this wave's allowlist, so true
  // `undefined` can't be returned without breaking their compile — see
  // crossFileNeeds. 'catalog' is the honest, build-safe interim: it is NOT a
  // fake baseline and the null cost still keeps the line out of priced CAPEX.
  return {
    category: entry.scmCategory,
    name: entry.name,
    spec: entry.spec,
    quantity: 1,
    unit: 'each',
    status: 'planned',
    unitCostUsd: entry.indicativeUnitCostUsd ?? null,
    totalCostUsd: entry.indicativeUnitCostUsd ?? null,
    priceSource: 'catalog',
    leadTimeMonthsLow: entry.leadTimeMonths?.low ?? 0,
    leadTimeMonthsHigh: entry.leadTimeMonths?.high ?? 0,
    leadTimeSource: hasLeadTime ? 'industry-baseline' : 'unknown',
    criticality: isOemSource && !hasLeadTime ? 'critical-path' : 'standard',
    substitutability: 'limited',
    equipmentCatalogEntryId: entry.id,
    ...(entry.powerPerUnitMw != null ? { powerPerUnitMw: entry.powerPerUnitMw } : {}),
    ...(oemBasis.generation ? { oemGenerationBasis: oemBasis.generation } : {}),
    ...(oemBasis.electricalMachine
      ? { oemElectricalMachineBasis: oemBasis.electricalMachine }
      : {}),
    ...((entry.supplierIds?.length ?? 0) > 0
      ? { shortlistedSupplierIds: [...(entry.supplierIds ?? [])] }
      : {}),
    ...(isOemSource
      ? {
        notes: hasLeadTime
          ? 'OEM technical source record. Lead time is a supplier-directory planning baseline; obtain a project quote.'
          : 'OEM technical source record. Price and lead time are unknown; obtain a project quote before schedule or CAPEX use.',
      }
      : {}),
  };
}
